import React, { type ComponentProps, type ReactNode } from 'react';

import { noop } from '@adsk/alloy-react-helpers';
import HighlightText from '@adsk/alloy-react-highlight-text';
import { AlloySuspense } from '@adsk/alloy-react-suspense';

import {
  Token,
  Placeholder,
  DefaultFooter,
  Selected,
  MenuItem,
  SuspenseFallback,
  MenuGroupHeader,
} from '../layout';
import NoAutocompleteResults from '../layout/Menu/NoAutocompleteResults';
import { VirtualizedMenuGroupHeader } from '../templates';
import GroupCountLabel from '../templates/Menu/GroupCountLabel';
import type {
  FooterProps,
  RenderGroupHeaderProps,
  RenderMenuItem,
} from '../types';
import { GenericItem, ItemToString } from '../types';

export const defaultRenderToken = ({
  key,
  ...props
}: ComponentProps<typeof Token>) => <Token key={key} {...props} />;

export const defaultRenderPlaceholder = (
  props?: ComponentProps<typeof Placeholder>,
) => <Placeholder {...props} />;

export const defaultRenderFooter = ({
  selectedItems,
  disableClear = !selectedItems || selectedItems.length === 0,
  ...props
}: FooterProps) => (
  <DefaultFooter
    selectedItems={selectedItems}
    disableClear={disableClear}
    {...props}
  />
);

export const defaultRenderItem = <TItem extends GenericItem>({
  selectedItem,
  itemToString,
}: {
  selectedItem?: TItem | null;
  itemToString: ItemToString<TItem>;
}) =>
  selectedItem ? (
    <Selected selectedItems={[selectedItem]} itemToString={itemToString} />
  ) : null;

export const defaultRenderItems = <TItem extends GenericItem>({
  selectedItems,
  itemToString,
}: {
  selectedItems: TItem[];
  itemToString: ItemToString<TItem>;
}) => <Selected selectedItems={selectedItems} itemToString={itemToString} />;

export const processMenuItemText = <TItem extends GenericItem>(
  item: TItem,
  inputValue?: string,
  enableSearchHighlight?: boolean,
) => {
  const hasLabel = (obj: TItem): obj is TItem & { label: ReactNode } =>
    'label' in obj;
  const hasSubtext = (obj: TItem): obj is TItem & { subtext: ReactNode } =>
    'subtext' in obj;

  const label = hasLabel(item) ? item.label : undefined;
  const subtext = hasSubtext(item) ? item.subtext : undefined;

  const renderText = (text: ReactNode) => {
    if (
      enableSearchHighlight &&
      inputValue?.trim() &&
      typeof text === 'string'
    ) {
      return <HighlightText text={text} matched={inputValue.trim()} />;
    }
    return text;
  };

  return {
    label,
    renderedLabel: renderText(label),
    subtext,
  };
};

export const defaultRenderMenuItem = <TItem extends GenericItem>(
  props: Parameters<RenderMenuItem<TItem>>[0],
) => {
  const {
    key,
    item,
    inputValue,
    enableSearchHighlight,
    text: _text,
    subtext: _subtext,
    ...cleanItemProps
  } = props as Parameters<RenderMenuItem<TItem>>[0] & {
    text?: unknown;
    subtext?: unknown;
  };
  const { renderedLabel, subtext } = processMenuItemText(
    item,
    inputValue,
    enableSearchHighlight,
  );
  return (
    <MenuItem
      key={key}
      text={renderedLabel}
      subtext={subtext}
      {...cleanItemProps}
    />
  );
};

const renderGroupHeader = ({
  item,
  index,
  expandedGroupKey,
  allowCollapsibleGroups,
  isAutocomplete,
  setExpandedGroupKey,
  showGroupItemCount,
  getItemProps,
  headerComponent: HeaderComponent,
  start = 0,
  height = 0,
  scrollToViewVirtual = noop,
  isVisibleVirtual = () => false,
}: RenderGroupHeaderProps & {
  headerComponent: typeof MenuGroupHeader | typeof VirtualizedMenuGroupHeader;
}) => {
  const itemProps = getItemProps?.({ item, index }) ?? {};
  const { isHighlighted, isSelected, text, subtext, isMultiple, ...domProps } =
    itemProps as Record<string, unknown> & {
      isHighlighted?: boolean;
      isSelected?: boolean;
      text?: string;
      subtext?: string;
      isMultiple?: boolean;
    };
  const handleHeaderClick = (e: React.MouseEvent) => {
    if (
      allowCollapsibleGroups &&
      (!isAutocomplete || (item.filteredItemCount ?? 0) > 0) &&
      item.groupKey !== null
    ) {
      setExpandedGroupKey?.(
        item.groupKey === expandedGroupKey ? '' : item.groupKey,
      );
    }
    (domProps as { onClick?: (e: React.MouseEvent) => void }).onClick?.(e);
  };

  return (
    <HeaderComponent
      allowCollapsibleGroups={
        allowCollapsibleGroups &&
        (!isAutocomplete || (item.filteredItemCount ?? 0) > 0)
      }
      onClick={handleHeaderClick}
      expanded={item.groupKey === expandedGroupKey}
      key={index}
      start={start}
      height={height}
      isVisibleVirtual={isVisibleVirtual}
      scrollToViewVirtual={scrollToViewVirtual}
    >
      {(() => {
        if (!showGroupItemCount) {
          return item.label;
        }

        const shouldShowSimpleCount =
          !isAutocomplete || (item.filteredItemCount ?? 0) >= item.itemCount;

        if (shouldShowSimpleCount) {
          return `${item.label} (${item.itemCount})`;
        }

        return (
          <AlloySuspense fallback={<SuspenseFallback />}>
            <GroupCountLabel
              label={item.label}
              count={item.filteredItemCount ?? 0}
              total={item.itemCount}
            />
          </AlloySuspense>
        );
      })()}
    </HeaderComponent>
  );
};

export const defaultRenderGroupHeader = (props: RenderGroupHeaderProps) => {
  return renderGroupHeader({ ...props, headerComponent: MenuGroupHeader });
};

export const defaultRenderVirtualGroupHeader = (
  props: RenderGroupHeaderProps,
) => {
  const isVirtualizedContext =
    props.start !== undefined && props.height !== undefined;
  const HeaderComponent = isVirtualizedContext
    ? VirtualizedMenuGroupHeader
    : MenuGroupHeader;

  return renderGroupHeader({
    ...props,
    headerComponent: HeaderComponent,
  });
};

export const defaultRenderNoAutocompleteResults = () => (
  <NoAutocompleteResults />
);
